import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { proxyAPI } from '../services/api';

function ProxiesList() {
  const [proxies, setProxies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchTags();
    fetchProxies();
  }, [selectedTag, filterActive]);

  const fetchTags = async () => {
    try {
      const response = await proxyAPI.getTags();
      setTags(response.data);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const fetchProxies = async () => {
    try {
      setLoading(true);
      const isActive = filterActive === 'all' ? undefined : filterActive === 'active';
      const response = await proxyAPI.getAll(selectedTag, isActive);
      setProxies(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this proxy?')) {
      return;
    }

    try {
      await proxyAPI.delete(id);
      fetchProxies();
    } catch (err) {
      alert('Failed to delete proxy: ' + err.message);
    }
  };

  // No masking - show full proxy URLs for developer convenience
  const maskProxyUrl = (url) => {
    return url;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <h2>Proxies</h2>
          <Link to="/proxies/create/edit" className="btn btn-primary">
            Create New Proxy
          </Link>
        </div>

        <div className="grid grid-2 mb-3">
          <div className="form-group">
            <label>Filter by Tag</label>
            <select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
            >
              <option value="">All Tags</option>
              {tags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
            >
              <option value="all">All</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger">
            Error: {error}
          </div>
        )}

        {proxies.length === 0 ? (
          <div className="alert alert-info">
            No proxies found. Create your first proxy to get started.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Proxy URL</th>
                  <th>Tags</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {proxies.map(proxy => (
                  <tr key={proxy._id}>
                    <td>
                      <Link to={`/proxies/${proxy._id}`}>
                        {proxy.name}
                      </Link>
                    </td>
                    <td>{proxy.description || '-'}</td>
                    <td>
                      <code style={{ fontSize: '0.85em' }}>
                        {maskProxyUrl(proxy.proxyUrl)}
                      </code>
                    </td>
                    <td>
                      {proxy.tags && proxy.tags.length > 0 ? (
                        proxy.tags.map(tag => (
                          <span key={tag} className="badge badge-secondary mr-1">
                            {tag}
                          </span>
                        ))
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <span className={`badge ${proxy.isActive ? 'badge-success' : 'badge-secondary'}`}>
                        {proxy.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group">
                        <Link
                          to={`/proxies/${proxy._id}/edit`}
                          className="btn btn-sm btn-secondary"
                        >
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(proxy._id)}
                          className="btn btn-sm btn-danger"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3">
          <p className="text-muted">
            Total: {proxies.length} {proxies.length === 1 ? 'proxy' : 'proxies'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default ProxiesList;
